import { useState, useEffect } from "react";
import { toast } from "react-toastify";

// Importe os novos styled-components

import TextSelect from "../../../components/ui/TextSelect/TextSelect";
import Loading from "../../../components/ui/Shared/Loading";
import {
  useUserDoctorQuery,
  useApproveUserMutation,
} from "@features/users";
import type { IApproveDoctorProps, INewList } from "./types";
import type { IGetUserDoctorResponseRows } from "@features/users";
import {
  Overlay,
  ModalContainer,
  ModalBody,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  IdentityRow,
  Avatar,
  IdentityCol,
  IdentityName,
  PendingPill,
  Divider,
  SectionTitle,
  SelectHint,
  CompareTable,
  CompareHead,
  CompareRow,
  CompareField,
  CompareTasy,
  CompareIcon,
  WarnBox,
  ModalFooter,
  SecondaryBtn,
  ApproveBtn,
} from "./styles";
import CheckIcon from "../../../assets/icons/CheckIcon";
import XIcon from "../../../assets/icons/XIcon";

export default function ApproveDoctor(props: IApproveDoctorProps) {
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<IApproveDoctorProps>();
  const [newList, setNewList] = useState<INewList[]>([]);
  const [dadosTasy, setDadosTasy] = useState<IGetUserDoctorResponseRows[]>([]);
  const [position, setPosition] = useState<number>(0);
  const approveMutation = useApproveUserMutation();

  function getNumberCRM(string: string) {
    const newNumber = string.indexOf("/");
    return dados?.crm.slice(0, newNumber);
  }

  const queryUf = dados?.crm.slice(-2);
  const queryNumberCrm = getNumberCRM(dados?.crm ?? "");
  const { data: doctorResponse } = useUserDoctorQuery(
    queryNumberCrm,
    queryUf,
  );

  useEffect(() => {
    if (doctorResponse?.doctors.rows) {
      setDadosTasy(doctorResponse.doctors.rows);
    }
  }, [doctorResponse]);

  async function AproveDoctorSenne(hashMedico: string, codmedico: string) {
    try {
      if (position === undefined) {
        toast.error("É necessário selecionar um médico da lista");
        return;
      }

      setLoading(true);

      const params = {
        hash_medico: hashMedico,
        codmedico: codmedico,
        role_id: 4,
      };

      await approveMutation.mutateAsync({
        id: Number(dados?.id),
        data: params as any,
      });

      props.onApproved?.();
    } catch (error: any) {
      console.error((error as any)?.message);
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
    } finally {
      setLoading(false);
      props.setOpenAprove(false);
    }
  }

  function listTasyData() {
    const list: INewList[] = [];
    if (dadosTasy) {
      dadosTasy.map((item, index: number) => {
        return list.push({
          id: index,
          name: item?.doctorName,
        });
      });
      setNewList(list);
    }
  }

  useEffect(() => {
    setDados(props);
  }, [props]);

  useEffect(() => {
    listTasyData();
  }, [dados, dadosTasy]);

  const initials = (dados?.name ?? "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const tasySel = dados ? dadosTasy[position] : undefined;
  const compareRows =
    dados && tasySel
      ? [
          {
            field: "Nome",
            cadastro: dados.name,
            tasy: tasySel.doctorName,
            ok:
              tasySel.doctorName.replace(/\s/g, "").toLowerCase() ===
              dados.name.replace(/\s/g, "").toLowerCase(),
          },
          {
            field: "CRM",
            cadastro: getNumberCRM(dados.crm) ?? "",
            tasy: `${tasySel.doctorCRM}/${tasySel.doctorUF}`,
            ok: tasySel.doctorCRM === getNumberCRM(dados.crm),
          },
        ]
      : [];

  return (
    <Overlay onClick={() => props.setOpenAprove(false)}>
      {loading && <Loading />}
      {dados && (
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalBody>
            <ModalHeader>
              <ModalTitle>Aprovar cadastro de médico</ModalTitle>
              <ModalSubtitle>Revise os dados e aprove ou recuse o acesso.</ModalSubtitle>
            </ModalHeader>

            <IdentityRow>
              <Avatar>{initials || "?"}</Avatar>
              <IdentityCol>
                <IdentityName>{dados.name}</IdentityName>
                <PendingPill>Aguardando aprovação</PendingPill>
              </IdentityCol>
            </IdentityRow>

            <Divider />

            <SectionTitle>Decisão</SectionTitle>

            <div>
              <TextSelect
                titleOption="Médicos"
                labelDescription="Escolha um Médico"
                options={newList}
                onChange={async (e) => setPosition(Number(e.target.value))}
              />
              <SelectHint>Registros encontrados no cadastro de médicos para este CRM/nome</SelectHint>
            </div>

            {tasySel ? (
              <CompareTable>
                <CompareHead>
                  <div />
                  <div>Solicitante</div>
                  <div>Cadastro de médicos</div>
                  <div />
                </CompareHead>
                {compareRows.map((r) => (
                  <CompareRow key={r.field}>
                    <CompareField>{r.field}</CompareField>
                    <div title={String(r.cadastro)}>{r.cadastro}</div>
                    <CompareTasy $ok={r.ok} title={String(r.tasy)}>
                      {r.tasy}
                    </CompareTasy>
                    <CompareIcon $ok={r.ok}>
                      {r.ok ? <CheckIcon /> : <XIcon />}
                    </CompareIcon>
                  </CompareRow>
                ))}
              </CompareTable>
            ) : doctorResponse && dadosTasy.length === 0 ? (
              <SelectHint role="status">
                Nenhum médico encontrado no cadastro de médicos para este CRM e UF.
              </SelectHint>
            ) : (
              <SelectHint>Selecione um médico da lista para comparar os dados.</SelectHint>
            )}

            <WarnBox>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <path d="M12 9v4M12 17h.01" />
              </svg>
              <span>Confira CRM e especialidade antes de aprovar.</span>
            </WarnBox>
          </ModalBody>

          <ModalFooter>
            <SecondaryBtn type="button" onClick={() => props.setOpenAprove(false)}>
              Voltar
            </SecondaryBtn>
            <ApproveBtn
              type="button"
              disabled={!tasySel}
              onClick={() => {
                if (tasySel) {
                  AproveDoctorSenne(tasySel.hash, tasySel.doctorId);
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Aprovar cadastro
            </ApproveBtn>
          </ModalFooter>
        </ModalContainer>
      )}
    </Overlay>
  );
}
